import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  Container,
  Box,
  Typography,
  Alert,
  Stack,
  Tabs,
  Tab,
  CircularProgress,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
  Snackbar,
  Grid,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import HistoryCard from '../components/HistoryCard';
import StatsCard from '../components/StatsCard';
import LogViewer, { type LogEntry } from '../components/LogViewer';
import { useProposalHistory } from '../hooks/useProposalHistory';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface HistoryStats {
  totalCost: number;
  weeklyCost: number;
  totalProposals: number;
  averageCost: number;
}

export default function History() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [stats, setStats] = useState<HistoryStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [logs, setLogs] = useState<Record<string, LogEntry[]>>({});
  const navigate = useNavigate();

  const {
    history,
    pagination,
    loading,
    error,
    fetchHistory,
    deleteProposal,
    retryProposal,
  } = useProposalHistory(statusFilter);

  // Setup Socket.IO for log streaming
  useEffect(() => {
    const newSocket = io(API_BASE_URL);

    // Listen for Claude Code logs
    newSocket.on('claude:log', (logEntry: LogEntry) => {
      setLogs(prev => ({
        ...prev,
        [logEntry.rfpId]: [...(prev[logEntry.rfpId] || []), logEntry]
      }));
    });

    return () => {
      newSocket.close();
    };
  }, []);

  // Fetch stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        const response = await axios.get(`${API_BASE_URL}/api/rfp/history/stats`, {
          withCredentials: true,
        });
        setStats(response.data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setStatusFilter(newValue);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    fetchHistory(page);
  };

  const handleViewProposal = (proposalId: string) => {
    navigate(`/app/dashboard?proposalId=${proposalId}`);
  };

  const handleRetryProposal = async (rfpId: string) => {
    try {
      await retryProposal(rfpId);
      setSnackbarMessage('Proposal generation restarted');
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage('Failed to retry proposal generation');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteClick = (proposalId: string) => {
    setProposalToDelete(proposalId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!proposalToDelete) return;

    try {
      await deleteProposal(proposalToDelete);
      setSnackbarMessage('Proposal deleted successfully');
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage('Failed to delete proposal');
      setSnackbarOpen(true);
    } finally {
      setDeleteDialogOpen(false);
      setProposalToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProposalToDelete(null);
  };

  const handleClearLogs = (rfpId: string) => {
    setLogs(prev => {
      const newLogs = { ...prev };
      delete newLogs[rfpId];
      return newLogs;
    });
  };

  const getEmptyMessage = () => {
    if (statusFilter === 'in_progress') {
      return 'No proposals are currently being generated';
    }
    if (statusFilter === 'completed') {
      return 'No completed proposals yet';
    }
    if (statusFilter === 'failed') {
      return 'No failed proposals';
    }
    return 'No proposal history yet. Upload an RFP to get started!';
  };

  const formatCost = (cost?: number) => {
    if (cost === undefined || cost === null) return 'N/A';
    return `$${cost.toFixed(2)}`;
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, md: 4 } }}>
      <Box mb={5}>
        <Typography
          variant="h4"
          component="h1"
          fontWeight={700}
          sx={{
            fontSize: { xs: '28px', md: '32px' },
            mb: 1,
            letterSpacing: '-0.01em'
          }}
        >
          Proposal History
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#64748B',
            fontSize: '16px',
            lineHeight: 1.6
          }}
        >
          View and manage all proposal generation attempts
        </Typography>
      </Box>

      {/* Stats Section */}
      {!statsLoading && stats && (
        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <StatsCard
              title="Total Cost"
              value={formatCost(stats.totalCost)}
              icon={<AttachMoneyIcon sx={{ fontSize: 20 }} />}
              color="#10B981"
              subtitle="All time"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <StatsCard
              title="Weekly Cost"
              value={formatCost(stats.weeklyCost)}
              icon={<CalendarTodayIcon sx={{ fontSize: 20 }} />}
              color="#3B82F6"
              subtitle="Last 7 days"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <StatsCard
              title="Total Proposals"
              value={stats.totalProposals}
              icon={<DescriptionIcon sx={{ fontSize: 20 }} />}
              color="#667eea"
              subtitle="Completed"
            />
          </Grid>
        </Grid>
      )}

      {/* Filter Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={statusFilter} onChange={handleTabChange}>
          <Tab label="All" value="" />
          <Tab label="In Progress" value="in_progress" />
          <Tab label="Completed" value="completed" />
          <Tab label="Failed" value="failed" />
        </Tabs>
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty State */}
      {!loading && !error && history.length === 0 && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          {getEmptyMessage()}
        </Alert>
      )}

      {/* History List */}
      {!loading && !error && history.length > 0 && (
        <Stack spacing={2}>
          {history.map(item => (
            <Box key={item.id}>
              <HistoryCard
                item={item}
                onView={handleViewProposal}
                onRetry={handleRetryProposal}
                onDelete={handleDeleteClick}
              />
              {/* Show logs for in-progress proposals */}
              {item.status === 'in_progress' && logs[item.rfpId] && logs[item.rfpId].length > 0 && (
                <Box mt={2}>
                  <LogViewer
                    logs={logs[item.rfpId]}
                    onClear={() => handleClearLogs(item.rfpId)}
                    defaultExpanded={false}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {/* Pagination */}
      {!loading && !error && pagination.totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={pagination.totalPages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Proposal?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this proposal? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
}
